import React, { useRef, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import {
  Send,
  Bot,
  User,
  Loader2,
  Trash2,
  Minimize2,
  Maximize2,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitization';
import { useChatStore } from '@/stores/chatStore';
import type { ChatMessage, IntentName, SuggestedPrompt } from '@/services/ai/types';
import { getAssistantRouteContext, getSuggestedPrompts } from '@/services/ai/suggestedPrompts';
import { classifyIntent } from '@/services/ai/intentRouter';
import { checkSafety } from '@/services/ai/safetyFilter';
import { agentControlRoomAPI } from '@/services/api';
import { AgenticRunInspector } from '@/components/agents/AgenticRunPanels';
import type { AgentActionPreview, AgentProviderIntent, AgentRunExecutionMode, AgentRunResponse } from '@/data/agentControlRoom';
import SuggestedPrompts from './SuggestedPrompts';
import GuidedFlowRenderer from './GuidedFlowRenderer';
import ChatModeToggle from './ChatModeToggle';

const RUNTIME_TEXT_INTENTS = new Set<IntentName>([
  'bot_status',
  'bot_list',
  'bot_diagnose',
  'bot_create_help',
  'agent_workflow',
  'strategy_action',
  'backtest_action',
  'connection_audit',
  'monitoring_triage',
  'compliance_review',
  'tax_reconcile',
  'report_analysis',
  'workspace_action',
]);

const PROVIDER_INTENTS = new Set<AgentProviderIntent>([
  'summary',
  'blocked',
  'approval',
  'paper',
  'mandate',
  'changes',
  'custom',
  'bot_status',
  'bot_list',
  'bot_diagnose',
  'bot_create_help',
  'agent_workflow',
  'recent_orders',
  'positions',
  'portfolio_overview',
  'risk_help',
  'strategy_help',
  'strategy_action',
  'backtest_help',
  'backtest_action',
  'connect_exchange',
  'connection_audit',
  'explain_error',
  'explain_notification',
  'monitoring_triage',
  'compliance_help',
  'compliance_review',
  'tax_reconcile',
  'report_analysis',
  'workspace_action',
  'kb_search',
]);

// ---- Lightweight markdown → HTML for chat bubbles ----

function escapeHtml(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function chatMarkdownToHtml(raw: string): string {
  if (!raw.trim()) return '';
  const escaped = escapeHtml(raw);

  // Inline formatting
  let html = escaped
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');

  // Process blocks (tables, lists, numbered lists, paragraphs)
  html = html
    .split(/\n{2,}/)
    .map((block) => {
      const trimmed = block.trim();

      // Markdown table block
      if (/^\|.+\|$/m.test(trimmed)) {
        const rows = trimmed.split('\n').filter(r => r.trim().startsWith('|'));
        // Skip separator row (|---|---|)
        const dataRows = rows.filter(r => !/^\|[\s-:|]+\|$/.test(r));
        if (dataRows.length === 0) return '';
        const headerRow = dataRows[0];
        const bodyRows = dataRows.slice(1);

        const parseCells = (row: string) =>
          row.split('|').slice(1, -1).map(c => c.trim());

        const ths = parseCells(headerRow).map(c => `<th>${c}</th>`).join('');
        const trs = bodyRows
          .map(r => {
            const tds = parseCells(r).map(c => `<td>${c}</td>`).join('');
            return `<tr>${tds}</tr>`;
          })
          .join('');

        return `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
      }

      // Unordered list
      if (/^(?:- |\* )/m.test(trimmed)) {
        const items = trimmed
          .split('\n')
          .filter(Boolean)
          .map((line) => line.replace(/^(?:- |\* )/, ''))
          .map((content) => `<li>${content}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }

      // Numbered list
      if (/^\d+\.\s/m.test(trimmed)) {
        const items = trimmed
          .split('\n')
          .filter(Boolean)
          .map((line) => line.replace(/^\d+\.\s*/, ''))
          .map((content) => `<li>${content}</li>`)
          .join('');
        return `<ol>${items}</ol>`;
      }

      return `<p>${trimmed.replace(/\n/g, '<br />')}</p>`;
    })
    .join('');

  return html;
}

function ChatMarkdown({ content }: { readonly content: string }) {
  const html = useMemo(
    () => sanitizeHtml(chatMarkdownToHtml(content)),
    [content],
  );
  return (
    <div
      className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-table:my-1 prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-headings:my-1 prose-th:border prose-th:border-slate-300 prose-td:border prose-td:border-slate-200 prose-th:bg-slate-200/50 prose-th:text-left prose-th:font-medium break-words [overflow-wrap:anywhere]"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function stableIdempotencyFragment(value: string): string {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
}

interface AIAssistantChatProps {
  readonly isMinimized?: boolean;
  readonly onMinimize?: () => void;
  readonly onMaximize?: () => void;
  readonly className?: string;
}

export default function AIAssistantChat({
  isMinimized = false,
  onMinimize,
  onMaximize,
  className,
}: AIAssistantChatProps) {
  const conversations = useChatStore(s => s.conversations);
  const activeConversationId = useChatStore(s => s.activeConversationId);
  const isStreaming = useChatStore(s => s.isStreaming);
  const sendMessage = useChatStore(s => s.sendMessage);
  const clearConversation = useChatStore(s => s.clearConversation);
  const mode = useChatStore(s => s.mode);
  const currentRoute = useChatStore(s => s.currentRoute);
  const routeContext = getAssistantRouteContext(currentRoute);
  const routePrompts = getSuggestedPrompts(currentRoute);

  const [inputValue, setInputValue] = React.useState('');
  const [runtimeRun, setRuntimeRun] = React.useState<AgentRunResponse | null>(null);
  const [runtimeError, setRuntimeError] = React.useState<string | null>(null);
  const [isRuntimePlanning, setIsRuntimePlanning] = React.useState(false);
  const [busyPreviewId, setBusyPreviewId] = React.useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const activeConversation = conversations.find(c => c.id === activeConversationId);
  const messages: ChatMessage[] = activeConversation?.messages ?? [];
  const hasConversationContent = messages.length > 0 || isStreaming;
  const showSuggestions = messages.length === 0 || (messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && !isStreaming);

  // Auto-scroll to bottom when new messages arrive or content changes
  useEffect(() => {
    if (messages.length === 0) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isStreaming) return;
    const userMessage = inputValue.trim();
    const topIntent = classifyIntent(userMessage)[0];
    if (topIntent && topIntent.confidence >= 0.85 && RUNTIME_TEXT_INTENTS.has(topIntent.intent)) {
      await handleComposeRuntimeAction(undefined, userMessage, topIntent.intent);
      return;
    }
    setInputValue('');
    await sendMessage(userMessage);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    clearConversation();
    setInputValue('');
    setRuntimeRun(null);
    setRuntimeError(null);
  };

  const buildRuntimePrompt = () =>
    inputValue.trim() ||
    routePrompts[0]?.text ||
    `Prepare governed next actions for ${routeContext.label}.`;

  const toRuntimeActionContext = (promptAction?: SuggestedPrompt) => ({
    route: currentRoute,
    assistantMode: mode,
    routeLabel: routeContext.label,
    capabilities: routeContext.capabilities,
    pageActions: routePrompts.map((prompt) => ({
      actionId: prompt.actionId ?? prompt.text,
      text: prompt.text,
      intent: prompt.intent,
      capability: prompt.capability,
      description: prompt.description,
      executionMode: prompt.executionMode,
      category: prompt.category,
      launchMode: prompt.launchMode ?? 'chat',
    })),
    pageAction: promptAction
      ? {
          actionId: promptAction.actionId ?? promptAction.text,
          text: promptAction.text,
          intent: promptAction.intent,
          capability: promptAction.capability,
          description: promptAction.description,
          executionMode: promptAction.executionMode,
          category: promptAction.category,
          launchMode: promptAction.launchMode ?? 'chat',
        }
      : undefined,
  });

  const inferExecutionMode = (
    prompt: string,
    promptAction?: SuggestedPrompt,
  ): AgentRunExecutionMode => {
    if (promptAction?.executionMode) return promptAction.executionMode;
    return /agent|draft|checklist|packet|note|fix|restart|create|export|rotate|kill-switch|archive|publish|role|session|submit|update|configure/i.test(prompt)
      ? 'proposal_draft'
      : 'brief_only';
  };

  const inferRuntimeIntent = (
    prompt: string,
    promptAction?: SuggestedPrompt,
    intentOverride?: IntentName,
  ): AgentProviderIntent => {
    const intent = promptAction?.intent ?? intentOverride ?? classifyIntent(prompt)[0]?.intent ?? routePrompts[0]?.intent ?? 'fallback';
    return PROVIDER_INTENTS.has(intent as AgentProviderIntent) ? (intent as AgentProviderIntent) : 'custom';
  };

  const makeIdempotencyScope = (promptAction?: SuggestedPrompt) =>
    (promptAction?.actionId ?? currentRoute)
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '') || 'workspace';

  const handleComposeRuntimeAction = async (
    promptAction?: SuggestedPrompt,
    promptOverride?: string,
    intentOverride?: IntentName,
  ) => {
    if (isStreaming || isRuntimePlanning) return;
    const prompt = promptOverride?.trim() || promptAction?.text || buildRuntimePrompt();
    const safety = checkSafety(prompt);
    if (!safety.safe) {
      setRuntimeError(safety.reason ?? 'Runtime actions cannot include sensitive information.');
      setInputValue(safety.redactedInput ?? '');
      return;
    }
    const executionMode = inferExecutionMode(prompt, promptAction);
    setIsRuntimePlanning(true);
    setRuntimeError(null);
    try {
      const response = await agentControlRoomAPI.createAgentRun(
        {
          prompt,
          intent: inferRuntimeIntent(prompt, promptAction, intentOverride),
          executionMode,
          clientContext: toRuntimeActionContext(promptAction),
        },
        executionMode === 'proposal_draft'
          ? `assistant-${makeIdempotencyScope(promptAction)}-${stableIdempotencyFragment(`${currentRoute}:${prompt}:${executionMode}`)}`
          : undefined,
      );
      setRuntimeRun(response);
      setInputValue('');
    } catch (error) {
      setRuntimeError(error instanceof Error ? error.message : 'Unable to create governed runtime run.');
    } finally {
      setIsRuntimePlanning(false);
    }
  };

  const handleSubmitActionPreview = async (preview: AgentActionPreview) => {
    setBusyPreviewId(preview.id);
    setRuntimeError(null);
    try {
      const response = await agentControlRoomAPI.submitActionPreview(preview.id, {
        idempotencyKey: `${preview.id}-assistant-submit`,
        requestedBy: 'rugira-assistant',
        note: 'Submitted from the Agent Command Layer action preview.',
      });
      setRuntimeRun(response.run);
    } catch (error) {
      setRuntimeError(error instanceof Error ? error.message : 'Unable to submit action preview.');
    } finally {
      setBusyPreviewId(null);
    }
  };

  const handleCancelActionPreview = async (preview: AgentActionPreview) => {
    setBusyPreviewId(preview.id);
    setRuntimeError(null);
    try {
      const response = await agentControlRoomAPI.cancelActionPreview(preview.id, {
        idempotencyKey: `${preview.id}-assistant-cancel`,
        reason: 'Cancelled from the Agent Command Layer.',
      });
      setRuntimeRun(response.run);
    } catch (error) {
      setRuntimeError(error instanceof Error ? error.message : 'Unable to cancel action preview.');
    } finally {
      setBusyPreviewId(null);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  if (isMinimized) {
    return (
      <Card className={cn('w-80 h-14 bg-white border shadow-lg', className)}>
        <CardContent className="p-3 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-brand-red">
                <Bot className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium">Agent Command Layer</p>
                <p className="text-xs text-slate-500">{routeContext.label}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onMaximize}
              className="h-8 w-8 p-0"
              data-testid="button-maximize-chat"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      data-testid="ai-assistant-panel"
      className={cn(
        'w-[26rem] max-w-[calc(100vw-2rem)] h-[calc(100vh-3rem)] max-h-[42rem] flex flex-col bg-white border shadow-xl',
        className,
      )}
    >
      {/* Header */}
      <div className="border-b bg-slate-50 p-3">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center space-x-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-brand-red shadow-sm">
              <Bot className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-900">Agent Command Layer</h3>
              <div className="flex min-w-0 items-center space-x-1">
                <div className="h-2 w-2 shrink-0 rounded-full bg-green-400"></div>
                <span className="shrink-0 text-xs text-slate-500">Context loaded</span>
                <span className="text-xs text-slate-300">/</span>
                <span className="min-w-0 truncate text-xs font-medium text-slate-600">{routeContext.label}</span>
              </div>
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-1">
            <ChatModeToggle />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearChat}
              className="h-8 w-8 p-0"
              title="Clear chat"
              data-testid="button-clear-chat"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onMinimize}
              className="h-8 w-8 p-0"
              title="Minimize"
              data-testid="button-minimize-chat"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="mt-2 rounded-xl border border-slate-200 bg-white/80 p-2.5 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {routeContext.headline}
          </p>
          <p className="mt-1 text-xs leading-4 text-slate-600">{routeContext.description}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {routeContext.capabilities.map((capability) => (
              <span
                key={capability}
                className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500"
              >
                {capability}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Messages */}
      {hasConversationContent && (
        <ScrollArea className="min-h-0 flex-1 bg-white p-4">
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex w-full',
                  message.role === 'user' ? 'justify-end' : 'justify-start',
                )}
              >
                <div
                  className={cn(
                    'max-w-[88%] min-w-0 rounded-lg px-3 py-2 text-sm shadow-sm',
                    message.role === 'user'
                      ? 'bg-brand-red text-white'
                      : 'bg-slate-100 text-slate-900 border border-slate-200',
                  )}
                >
                  <div className="flex min-w-0 items-start space-x-2">
                    {message.role === 'assistant' && (
                      <Bot className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                    )}
                    {message.role === 'user' && (
                      <User className="mt-0.5 h-4 w-4 shrink-0 text-red-50" />
                    )}
                    <div className="min-w-0 flex-1">
                      {message.role === 'assistant' && message.status !== 'streaming' ? (
                        <ChatMarkdown content={message.content} />
                      ) : (
                        <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                          {message.content}
                          {message.status === 'streaming' && (
                            <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-brand-red" />
                          )}
                        </div>
                      )}
                      <div
                        className={cn(
                          'text-xs mt-1',
                          message.role === 'user' ? 'text-red-50' : 'text-slate-500',
                        )}
                      >
                        {formatTimestamp(message.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {isStreaming && messages[messages.length - 1]?.status !== 'streaming' && (
              <div className="flex w-full justify-start">
                <div className="max-w-[88%] min-w-0 bg-slate-100 rounded-lg px-3 py-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Bot className="h-4 w-4 text-brand-red" />
                    <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                    <span className="text-slate-500">Preparing governed run...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      )}

      {(runtimeRun || runtimeError || isRuntimePlanning) && (
        <ScrollArea
          className={cn(
            'min-h-0 bg-white p-3',
            hasConversationContent ? 'max-h-80 border-t border-slate-100' : 'flex-1',
          )}
        >
          <div className="space-y-3">
            {isRuntimePlanning ? (
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-700">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  <span>Checking governed runtime scope...</span>
                </div>
              </div>
            ) : null}
            {runtimeError ? (
              <div className="rounded-xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">
                {runtimeError}
              </div>
            ) : null}
            {runtimeRun ? (
              <AgenticRunInspector
                run={runtimeRun}
                title="Assistant action run"
                compactTimeline
                busyPreviewId={busyPreviewId}
                onSubmitPreview={handleSubmitActionPreview}
                onCancelPreview={handleCancelActionPreview}
              />
            ) : null}
          </div>
        </ScrollArea>
      )}

      {/* Guided Flow */}
      <GuidedFlowRenderer />

      {/* Suggested Prompts */}
      {showSuggestions && (
        <SuggestedPrompts
          onLaunchRuntimeAction={handleComposeRuntimeAction}
          isRuntimePlanning={isRuntimePlanning}
        />
      )}

      {/* Input */}
      <div className="border-t bg-slate-50 bg-opacity-100 p-3">
        <div className="flex space-x-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={routeContext.placeholder}
            disabled={isStreaming}
            className="flex-1"
            data-testid="input-chat-message"
          />
          <Button
            onClick={() => handleComposeRuntimeAction()}
            disabled={isStreaming || isRuntimePlanning}
            size="sm"
            variant="outline"
            className="px-3"
            title="Compose governed action"
            aria-label="Compose governed action"
            data-testid="button-compose-runtime-action"
          >
            {isRuntimePlanning ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
          </Button>
          <Button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isStreaming}
            size="sm"
            className="px-3"
            data-testid="button-send-message"
          >
            {isStreaming ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        <div className="mt-2 flex items-center justify-center">
          <span className="text-xs text-slate-400">
            {mode === 'support' ? 'Support' : 'Analysis'} Mode
          </span>
        </div>
      </div>
    </Card>
  );
}
